import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { JobApplication, JobOpening } from '../types/api';
import {
  CheckIcon,
  XMarkIcon,
  StarIcon,
  DocumentArrowDownIcon,
  UserIcon,
  PlusIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import RichTextEditor from '../components/RichTextEditor';
import RichTextDisplay from '../components/RichTextDisplay';

const ApplicationsPage: React.FC = () => {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [jobs, setJobs] = useState<JobOpening[]>([]);
  const [loading, setLoading] = useState(true);
  const [showManualForm, setShowManualForm] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const [notesText, setNotesText] = useState('');
  const [manualFormData, setManualFormData] = useState({
    job_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    motivation_letter: '',
    status: 'pending',
  });
  const [submitting, setSubmitting] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get filters from URL parameters, default to 'all'
  const selectedStatus = searchParams.get('status') || 'all';
  const selectedJobId = searchParams.get('job_id') || 'all';

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [selectedStatus, selectedJobId]);

  // Handle escape key to close modals
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (showNotesModal) {
        setShowNotesModal(false);
        setSelectedApplication(null);
        setNotesText('');
      }
      if (showManualForm) {
        setShowManualForm(false);
      }
    }
  }, [showNotesModal, showManualForm]);

  useEffect(() => {
    if (showNotesModal || showManualForm) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showNotesModal, showManualForm, handleEscapeKey]);

  const fetchApplications = async () => {
    try {
      let apps: JobApplication[];
      
      // If both filters are 'all', get all applications
      if (selectedStatus === 'all' && selectedJobId === 'all') {
        apps = await apiService.getAllApplications();
      } else {
        // If we have specific filters, we need to filter the results
        apps = await apiService.getAllApplications();
        
        // Filter by status if specified
        if (selectedStatus !== 'all') {
          apps = apps.filter(app => app.status === selectedStatus);
        }
        
        // Filter by job if specified
        if (selectedJobId !== 'all') {
          apps = apps.filter(app => app.job_id === parseInt(selectedJobId));
        }
      }
      
      setApplications(apps);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const allJobs = await apiService.getAllJobs();
      setJobs(allJobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  };

  const handleStatusChange = (status: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (status === 'all') {
      newParams.delete('status');
    } else {
      newParams.set('status', status);
    }
    setSearchParams(newParams);
  };

  const handleJobChange = (jobId: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (jobId === 'all') {
      newParams.delete('job_id');
    } else {
      newParams.set('job_id', jobId);
    }
    setSearchParams(newParams);
  };

  const clearAllFilters = () => {
    setSearchParams({});
  };


  const handleUpdateStatus = async (id: number, status: string) => {
    try {
      await apiService.updateApplication(id, { status: status as any });
      fetchApplications();
    } catch (error) {
      console.error(`Failed to update application status to ${status}:`, error);
    }
  };

  const handleOpenNotes = (application: JobApplication) => {
    setSelectedApplication(application);
    setNotesText(application.notes || '');
    setShowNotesModal(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedApplication) return;
    
    try {
      await apiService.updateApplication(selectedApplication.id, { notes: notesText });
      setShowNotesModal(false);
      setSelectedApplication(null);
      setNotesText('');
      fetchApplications();
    } catch (error) {
      console.error('Failed to save notes:', error);
    }
  };

  const handleManualFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create a manual application without resume file
      await apiService.createManualApplication({
        job_id: parseInt(manualFormData.job_id),
        first_name: manualFormData.first_name,
        last_name: manualFormData.last_name,
        email: manualFormData.email,
        phone: manualFormData.phone,
        motivation_letter: manualFormData.motivation_letter,
        status: manualFormData.status,
      });
      
      // Reset form and hide it
      setManualFormData({
        job_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        motivation_letter: '',
        status: 'pending',
      });
      setShowManualForm(false);
      fetchApplications();
    } catch (error: any) {
      console.error('Failed to create manual application:', error);
      alert(error.response?.data?.error || 'Failed to create application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setManualFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDownloadResume = async (id: number) => {
    try {
      const blob = await apiService.downloadResume(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resume-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download resume:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'shortlisted':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'selected':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'shortlisted':
        return '⭐';
      case 'rejected':
        return '❌';
      case 'selected':
        return '✅';
      default:
        return '📄';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Applications Management
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Review and manage job applications
            {selectedStatus !== 'all' && (
              <span className="ml-1">
                • {selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)}
              </span>
            )}
            {selectedJobId !== 'all' && (
              <span className="ml-1">
                • {jobs.find(job => job.id === parseInt(selectedJobId))?.title || ''}
              </span>
            )}
            {applications.length > 0 && (
              <span className="ml-1">
                • Showing {applications.length} application{applications.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <select
              value={selectedStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="rejected">Rejected</option>
              <option value="selected">Selected</option>
            </select>
            <select
              value={selectedJobId}
              onChange={(e) => handleJobChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Jobs</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title}
                </option>
              ))}
            </select>
          </div>
          {(selectedStatus !== 'all' || selectedJobId !== 'all') && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear All Filters
            </button>
          )}
          <button
            onClick={() => setShowManualForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Application
          </button>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {applications.map((application) => (
            <li key={application.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <h3 className="text-lg font-medium text-gray-900">
                          {application.first_name} {application.last_name}
                        </h3>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                          {getStatusIcon(application.status)} {application.status}
                        </span>
                        {application.job_title && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {application.job_title}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {application.email}
                        {application.phone && ` • ${application.phone}`}
                      </p>
                      <p className="text-xs text-gray-400">
                        Applied: {new Date(application.created_at).toLocaleDateString()}
                        {application.notes && (
                          <span className="ml-2 text-blue-600">
                            📝 Has notes
                          </span>
                        )}
                      </p>
                      {application.notes && (
                        <div className="mt-2 text-xs text-gray-600 max-w-md">
                          <div className="line-clamp-2">
                            <RichTextDisplay 
                              content={application.notes} 
                              className="text-xs"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/backoffice/applications/${application.id}`}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      title="View Details"
                    >
                      <UserIcon className="h-4 w-4 mr-1" />
                      View
                    </Link>
                    <button
                      onClick={() => handleDownloadResume(application.id)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      title="Download Resume"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4" />
                    </button>
                    
                    {/* Status Transition Buttons - All available for all applications */}
                    <button
                      onClick={() => handleUpdateStatus(application.id, 'pending')}
                      disabled={application.status === 'pending'}
                      className={`inline-flex items-center px-3 py-1 border text-sm font-medium rounded-md ${
                        application.status === 'pending'
                          ? 'border-yellow-300 text-yellow-700 bg-yellow-50 cursor-default'
                          : 'border-yellow-300 text-yellow-700 bg-white hover:bg-yellow-50'
                      } disabled:opacity-50`}
                      title="Mark as Pending"
                    >
                      ⏳ Pending
                    </button>
                    
                    <button
                      onClick={() => handleUpdateStatus(application.id, 'shortlisted')}
                      disabled={application.status === 'shortlisted'}
                      className={`inline-flex items-center px-3 py-1 border text-sm font-medium rounded-md ${
                        application.status === 'shortlisted'
                          ? 'border-blue-300 text-blue-700 bg-blue-50 cursor-default'
                          : 'border-blue-300 text-blue-700 bg-white hover:bg-blue-50'
                      } disabled:opacity-50`}
                      title="Mark as Shortlisted"
                    >
                      <StarIcon className="h-4 w-4 mr-1" />
                      Shortlist
                    </button>
                    
                    <button
                      onClick={() => handleUpdateStatus(application.id, 'rejected')}
                      disabled={application.status === 'rejected'}
                      className={`inline-flex items-center px-3 py-1 border text-sm font-medium rounded-md ${
                        application.status === 'rejected'
                          ? 'border-red-300 text-red-700 bg-red-50 cursor-default'
                          : 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                      } disabled:opacity-50`}
                      title="Mark as Rejected"
                    >
                      <XMarkIcon className="h-4 w-4 mr-1" />
                      Reject
                    </button>
                    
                    <button
                      onClick={() => handleUpdateStatus(application.id, 'selected')}
                      disabled={application.status === 'selected'}
                      className={`inline-flex items-center px-3 py-1 border text-sm font-medium rounded-md ${
                        application.status === 'selected'
                          ? 'border-green-300 text-green-700 bg-green-50 cursor-default'
                          : 'border-green-300 text-green-700 bg-white hover:bg-green-50'
                      } disabled:opacity-50`}
                      title="Mark as Selected"
                    >
                      <CheckIcon className="h-4 w-4 mr-1" />
                      Select
                    </button>
                    
                    <button
                      onClick={() => handleOpenNotes(application)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      title="View/Edit Notes"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Manual Application Form Modal */}
      {showManualForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add Manual Application</h3>
                <button
                  onClick={() => setShowManualForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleManualFormSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="job_id" className="block text-sm font-medium text-gray-700">
                      Job Position *
                    </label>
                    <select
                      id="job_id"
                      name="job_id"
                      value={manualFormData.job_id}
                      onChange={handleManualFormChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a job</option>
                      {jobs.map((job) => (
                        <option key={job.id} value={job.id}>
                          {job.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                      Status *
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={manualFormData.status}
                      onChange={handleManualFormChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="shortlisted">Shortlisted</option>
                      <option value="rejected">Rejected</option>
                      <option value="selected">Selected</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={manualFormData.first_name}
                      onChange={handleManualFormChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="John"
                    />
                  </div>

                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={manualFormData.last_name}
                      onChange={handleManualFormChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={manualFormData.email}
                      onChange={handleManualFormChange}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="john.doe@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={manualFormData.phone}
                      onChange={handleManualFormChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="motivation_letter" className="block text-sm font-medium text-gray-700">
                    Motivation Letter (Optional)
                  </label>
                  <div className="mt-1">
                    <RichTextEditor
                      value={manualFormData.motivation_letter}
                      onChange={(value) => setManualFormData(prev => ({ ...prev, motivation_letter: value }))}
                      placeholder="Optional motivation letter for manual applications... You can use formatting options like bold, italic, lists, etc."
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Optional for manual applications. For visitor applications, this field is required with minimum 400 characters.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowManualForm(false)}
                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? 'Creating...' : 'Create Application'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && selectedApplication && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-6 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 h-5/6 shadow-lg rounded-md bg-white flex flex-col resizable-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Notes for {selectedApplication.first_name} {selectedApplication.last_name}</h3>
              <button
                onClick={() => setShowNotesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 min-h-0">
              <RichTextEditor
                value={notesText}
                onChange={setNotesText}
                placeholder="Add notes here... You can use formatting options like bold, italic, lists, etc."
                className="h-full"
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-gray-200">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationsPage;
